import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Expense } from '../types';

export class ExpenseService {
  // Add new expense
  static async addExpense(
    salonId: string,
    expenseData: Omit<Expense, 'id' | 'salonId' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'expenses'), {
        ...expenseData,
        salonId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      return docRef.id;
    } catch (error) {
      console.error('Error adding expense:', error);
      throw new Error('Failed to add expense');
    }
  }

  // Update expense
  static async updateExpense(
    expenseId: string,
    updates: Partial<Omit<Expense, 'id' | 'salonId' | 'createdAt'>>
  ): Promise<void> {
    try {
      const expenseRef = doc(db, 'expenses', expenseId);
      await updateDoc(expenseRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating expense:', error);
      throw new Error('Failed to update expense');
    }
  }

  // Get all expenses for a salon
  static async getExpenses(salonId: string): Promise<Expense[]> {
    try {
      const q = query(
        collection(db, 'expenses'),
        where('salonId', '==', salonId)
      );

      const querySnapshot = await getDocs(q);
      const expenses: Expense[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        expenses.push({
          id: doc.id,
          ...data,
          date: data.date.toDate(),
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as Expense);
      });

      // Sort client-side instead of requiring composite index
      return expenses.sort((a, b) => b.date.getTime() - a.date.getTime());
    } catch (error) {
      console.error('Error getting expenses:', error);
      throw new Error('Failed to get expenses');
    }
  }

  // Get expenses within a date range
  static async getExpensesInRange(
    salonId: string,
    startDate: Date,
    endDate: Date
  ): Promise<Expense[]> {
    try {
      const allExpenses = await this.getExpenses(salonId);
      return allExpenses.filter(expense =>
        expense.date >= startDate && expense.date <= endDate
      );
    } catch (error) {
      console.error('Error getting expenses in range:', error);
      throw new Error('Failed to get expenses in range');
    }
  }

  // Get expense by ID
  static async getExpense(expenseId: string): Promise<Expense | null> {
    try {
      const docRef = doc(db, 'expenses', expenseId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          date: data.date.toDate(),
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as Expense;
      }
      return null;
    } catch (error) {
      console.error('Error getting expense:', error);
      throw new Error('Failed to get expense');
    }
  }

  // Delete expense
  static async deleteExpense(expenseId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'expenses', expenseId));
    } catch (error) {
      console.error('Error deleting expense:', error);
      throw new Error('Failed to delete expense');
    }
  }

  // Get total expenses for a salon within a date range
  static async getTotalExpensesInRange(
    salonId: string,
    startDate: Date,
    endDate: Date
  ): Promise<number> {
    try {
      const expenses = await this.getExpensesInRange(salonId, startDate, endDate);
      return expenses.reduce((total, expense) => total + expense.amount, 0);
    } catch (error) {
      console.error('Error calculating total expenses:', error);
      throw new Error('Failed to calculate total expenses');
    }
  }

  // Get expenses grouped by category
  static async getExpensesByCategory(
    salonId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<{ [category: string]: number }> {
    try {
      let expenses = await this.getExpenses(salonId);

      // Filter by date range if provided
      if (startDate && endDate) {
        expenses = expenses.filter(expense =>
          expense.date >= startDate && expense.date <= endDate
        );
      }

      // Group by category
      const categoryTotals: { [category: string]: number } = {};
      expenses.forEach(expense => {
        categoryTotals[expense.category] = (categoryTotals[expense.category] || 0) + expense.amount;
      });

      return categoryTotals;
    } catch (error) {
      console.error('Error getting expenses by category:', error);
      throw new Error('Failed to get expenses by category');
    }
  }
}
